import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { Loader2, Save, ImagePlus, X } from "lucide-react";

const CARD_TOGGLES = [
  { k: "card_show_type_detail", label: "Facility type detail", desc: "e.g. ALR/ACF next to the facility type." },
  { k: "card_show_availability", label: "Available beds badge", desc: "The open-bed / availability pill." },
  { k: "card_show_location", label: "City, state & county", desc: "Location line under the name." },
  { k: "card_show_phone", label: "Phone number", desc: "Contact phone on the card." },
  { k: "card_show_licensed_beds", label: "Licensed bed count", desc: "The '<n> licensed' figure." },
];

// Defined at module scope so their identity is stable across renders — otherwise
// the inputs remount on every keystroke and lose focus.
function Field({ form, set, label, k, textarea }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`set-${k}`}>{label}</Label>
      {textarea
        ? <Textarea id={`set-${k}`} rows={3} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-xl" data-testid={`set-${k}`} />
        : <Input id={`set-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-xl" data-testid={`set-${k}`} />}
    </div>
  );
}

function ImageField({ form, set, uploading, uploadImage, label, k }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`upload-${k}`}>{label}</Label>
      <div className="flex items-center gap-3">
        {form[k]
          ? <div className="relative"><img src={form[k]} alt={`${label} preview`} className="h-20 w-32 rounded-xl object-cover border border-[#E8E6DF]" /><button type="button" onClick={() => set(k, "")} className="absolute -top-2 -right-2 bg-white rounded-full border border-[#E8E6DF] p-0.5" data-testid={`clear-${k}`}><X className="h-3.5 w-3.5" /></button></div>
          : <div className="h-20 w-32 rounded-xl border-2 border-dashed border-[#E8E6DF] flex items-center justify-center text-[#5C6660] text-xs">No image</div>}
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-[#E8E6DF] cursor-pointer hover:border-[#1A365D] text-sm" data-testid={`upload-${k}`}>
          {uploading === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Upload
          <input id={`upload-${k}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(k, e)} />
        </label>
      </div>
    </div>
  );
}

function ColorField({ form, set, label, k }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`color-${k}`}>{label}</Label>
      <div className="flex items-center gap-2">
        <input id={`color-${k}`} type="color" value={form[k] || "#000000"} onChange={(e) => set(k, e.target.value)} className="h-11 w-14 rounded-lg border border-[#E8E6DF] cursor-pointer" data-testid={`color-${k}`} />
        <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-xl font-mono-data uppercase" />
      </div>
    </div>
  );
}

export function SettingsEditor() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => { setForm(settings); }, [settings]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); await refresh(); toast.success("Site updated — changes are live."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const uploadImage = async (k, e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(k);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(k, data.url); }
    catch { toast.error("Upload failed"); } finally { setUploading(null); }
  };

  return (
    <div className="max-w-[720px] space-y-8">
      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Brand & homepage text</h3>
        <div className="space-y-4">
          <Field form={form} set={set} label="Brand name" k="brand_name" />
          <Field form={form} set={set} label="Homepage headline" k="hero_title" textarea />
          <Field form={form} set={set} label="Homepage subtext" k="hero_subtitle" textarea />
          <Field form={form} set={set} label="Homepage button text" k="hero_cta" />
        </div>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Marketing page images</h3>
        <div className="space-y-4">
          <ImageField form={form} set={set} uploading={uploading} uploadImage={uploadImage} label="Homepage hero image" k="hero_image" />
          <ImageField form={form} set={set} uploading={uploading} uploadImage={uploadImage} label="For-Facilities hero image" k="for_fac_image" />
        </div>
        <p className="text-xs text-[#5C6660] mt-3">Leave blank to use the default photo. Recommended landscape (~1100×460).</p>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">For-Facilities page text</h3>
        <div className="space-y-4">
          <Field form={form} set={set} label="For-Facilities headline" k="for_fac_title" textarea />
          <Field form={form} set={set} label="For-Facilities subtext" k="for_fac_subtitle" textarea />
        </div>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Section & plan titles</h3>
        <p className="text-sm text-[#5C6660] mb-4">Edit the headings shown on the About and For-Facilities pages, and the plan names.</p>
        <div className="space-y-4">
          <Field form={form} set={set} label="About: “How it works” heading" k="about_how_title" />
          <Field form={form} set={set} label="About: “How we make money” heading" k="about_money_title" />
          <Field form={form} set={set} label="About: “Transparency” heading" k="about_transparency_title" />
          <Field form={form} set={set} label="About: Contact heading" k="about_contact_title" />
          <Field form={form} set={set} label="About: Contact intro" k="about_contact_intro" textarea />
          <Field form={form} set={set} label="Plans section heading" k="ff_plans_title" />
          <Field form={form} set={set} label="Plans section subtext" k="ff_plans_subtitle" textarea />
          <Field form={form} set={set} label="Pricing matrix heading" k="ff_matrix_title" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field form={form} set={set} label="Free plan name" k="plan_free_name" />
            <Field form={form} set={set} label="Premium plan name" k="plan_premium_name" />
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">About page content</h3>
        <div className="space-y-4">
          <Field form={form} set={set} label="About headline" k="about_title" textarea />
          <Field form={form} set={set} label="About intro paragraph" k="about_intro" textarea />
          <Field form={form} set={set} label="“How we make money” text" k="about_money_body" textarea />
          <Field form={form} set={set} label="“Why we're more transparent” intro" k="about_transparency_intro" textarea />
        </div>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Facility type categories</h3>
        <p className="text-sm text-[#5C6660] mb-4">Rename the type labels shown on cards and in the search filter, and choose which appear as filter options.</p>
        <div className="space-y-3 bg-white border border-[#E8E6DF] rounded-2xl p-5" data-testid="category-editor">
          {(form.facility_categories || []).map((c, i) => (
            <div key={c.key} className="flex items-center gap-3">
              <Input
                value={c.label}
                onChange={(e) => {
                  const next = [...form.facility_categories];
                  next[i] = { ...c, label: e.target.value };
                  set("facility_categories", next);
                }}
                className="rounded-xl flex-1"
                data-testid={`category-label-${c.key}`}
              />
              <label className="flex items-center gap-2 whitespace-nowrap text-sm text-[#4A534E]">
                <Switch
                  checked={c.enabled !== false}
                  onCheckedChange={(v) => {
                    const next = [...form.facility_categories];
                    next[i] = { ...c, enabled: v };
                    set("facility_categories", next);
                  }}
                  data-testid={`category-enabled-${c.key}`}
                />
                Show in filter
              </label>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Payment type options</h3>
        <p className="text-sm text-[#5C6660] mb-4">The list of payment types facilities can choose from (one per line). Directors and admins pick which they accept per facility.</p>
        <Textarea
          rows={6}
          data-testid="set-payment_type_options"
          value={(form.payment_type_options || []).join("\n")}
          onChange={(e) => set("payment_type_options", e.target.value.split("\n"))}
          className="rounded-xl"
        />
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Facility card display</h3>
        <p className="text-sm text-[#5C6660] mb-4">Choose which details appear on the facility cards in search results.</p>
        <div className="space-y-1 bg-white border border-[#E8E6DF] rounded-2xl p-5" data-testid="card-toggles">
          {CARD_TOGGLES.map((t) => (
            <div key={t.k} className="flex items-center justify-between gap-3 py-2 border-b border-[#F2F1EC] last:border-0">
              <div><div className="text-sm font-medium text-[#1C211F]">{t.label}</div><div className="text-xs text-[#5C6660]">{t.desc}</div></div>
              <Switch checked={form[t.k] !== false} onCheckedChange={(v) => set(t.k, v)} data-testid={`toggle-${t.k}`} />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Homepage stat counters</h3>
        <div className="bg-white border border-[#E8E6DF] rounded-2xl p-5">
          <div className="text-sm font-medium text-[#1C211F] mb-1">What to show in the hero</div>
          <div className="text-xs text-[#5C6660] mb-4">Choose how the launch stats appear. Use "Total only" while open-bed and verified numbers are still low.</div>
          <div className="inline-flex flex-wrap gap-2" data-testid="landing-stats-mode">
            {[
              { v: "full", label: "Full stats", desc: "Facilities · Open beds · Verified" },
              { v: "total_only", label: "Total only", desc: "Just the facility count" },
              { v: "hidden", label: "Hidden", desc: "Show no counters" },
            ].map((o) => {
              const active = (form.landing_stats_mode || "full") === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set("landing_stats_mode", o.v)}
                  data-testid={`landing-stats-mode-${o.v}`}
                  className={`text-left rounded-xl border px-4 py-3 transition-colors ${active ? "border-[#1A365D] bg-[#E3F3ED]" : "border-[#E8E6DF] bg-white hover:border-[#C9C4B8]"}`}
                >
                  <div className={`text-sm font-medium ${active ? "text-[#1A365D]" : "text-[#1C211F]"}`}>{o.label}</div>
                  <div className="text-xs text-[#5C6660]">{o.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section>
        <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-4">Design colors</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <ColorField form={form} set={set} label="Primary" k="color_primary" />
          <ColorField form={form} set={set} label="Primary (hover)" k="color_primary_hover" />
          <ColorField form={form} set={set} label="Secondary / accent buttons" k="color_secondary" />
          <ColorField form={form} set={set} label="Soft accent background" k="color_accent" />
        </div>
        <p className="text-xs text-[#5C6660] mt-3">Colors apply across buttons, badges, and highlights site-wide instantly after saving.</p>
      </section>

      <Button onClick={save} disabled={saving} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11 px-8" data-testid="save-settings-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save changes</>}
      </Button>
    </div>
  );
}
